import React, { useEffect, useState } from "react";
import axios from "axios";
import { Authorization } from "../../Context/Authorization";
import Preloader from "../../shared/PreLoaders";
import "../../dashboard/reporttable.css";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";

const inputStyle = { width: "100%", padding: "9px 12px", border: "1.5px solid #E2E8F0", borderRadius: 8, fontSize: 13, color: "#1A202C", background: "#fff", };

const labelStyle = { fontSize: 12, fontWeight: 600, color: "#2D3748", marginBottom: 6, display: "flex", alignItems: "center", gap: 5, };
const TogglePill = ({
    label,
    icon,
    checked,
    onChange,
    disabled = false,
}) => (
    <label
        style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "8px 16px",
            borderRadius: 999,
            border: "1px solid #2E7D32",
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.5 : 1,
            background: checked ? "#E8F5E9" : "#fff",
        }}
    >
        <input
            type="checkbox"
            checked={checked}
            disabled={disabled}
            onChange={onChange}
            style={{ display: "none" }}
        />

        <i className={`mdi ${icon}`} />
        {label}
    </label>
);
const BankBalanceReview = () => {
    const Api = process.env.REACT_APP_API;
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    /* Master Data */
    const [circles, setCircles] = useState([]);
    const [divisions, setDivisions] = useState([]);
    const [jfmcTypes, setJfmcTypes] = useState([]);
    /* Report */
    const [meetingData, setMeetingData] = useState([]);
    /* Filters */
    const today = new Date();
    const [selectedMonth, setSelectedMonth] = useState(
        `${today.getFullYear()}-${String(today.getMonth()).padStart(2, "0")}`
    );
    const [year, month] = selectedMonth.split("-");
    const [selectedCircle, setSelectedCircle] = useState("");
    const [selectedDivision, setSelectedDivision] = useState("");
    const [selectedRange, setSelectedRange] = useState("");
    const [selectedJfmcType, setSelectedJfmcType] = useState("");
    const [showCircle, setShowCircle] = useState(false);
    const [showDivision, setShowDivision] = useState(false);
    const [selectedLevel, setSelectedLevel] = useState("circle");
    const [expandedDivisions, setExpandedDivisions] = useState({});
    const [expandedRanges, setExpandedRanges] = useState({});
    /* -------------------------------------- */
    const fetchMasterData = async () => {
        try {
            const config = Authorization();

            const [circleRes, divisionRes, jfmcRes] = await Promise.all([
                axios.get(`${Api}/api/circles`, config),
                axios.get(`${Api}/api/divisions`, config),
                axios.get(`${Api}/api/jfmc-types`, config),
            ]);

            setCircles(circleRes.data || []);
            setDivisions(divisionRes.data || []);

            const jfmcTypeList = jfmcRes.data || [];
            setJfmcTypes(jfmcTypeList);

            // Automatically select VFC JFMC Type
            const defaultJfmcType = jfmcTypeList.find(
                (item) => item.jfmcTypeName === "VFC"
            );
            if (defaultJfmcType) {
                setSelectedJfmcType(defaultJfmcType._id);
            }
        } catch (err) {
            console.error(err);
            setError("Failed to load filters.");
        }
    };
    /* -------------------------------------- */
    const fetchMeetingReview = async (jfmcTypeId = selectedJfmcType) => {
        try {
            setLoading(true);

            const config = Authorization();

            const res = await axios.get(
                `${Api}/api/closing-balance/review`,
                {
                    ...config,
                    params: {
                        month,
                        year,
                        circleId: selectedCircle || undefined,
                        divisionId: selectedDivision || undefined,
                        rangeId: selectedRange || undefined,
                        jfmcTypeId,
                    },
                }
            );

            setMeetingData(res.data.data || []);
            console.log("API Response", res.data.data);
            console.log("Total Records", res.data.data.length);
        } catch (error) {
            console.error("Bank balance Review Error:", error);

            setMeetingData([])
            // Optional: show a toast or alert
            toast.error(
                error.response?.data?.message || "Failed to load Bank Balance review data."
            );

        } finally {
            setLoading(false);
        }
    };

    /* -------------------------------------- */
    useEffect(() => {
        fetchMasterData();
    }, []);
    /* -------------------------------------- */
    useEffect(() => {
        if (!selectedJfmcType) return;

        fetchMeetingReview();
    }, [selectedMonth, selectedCircle, selectedDivision, selectedJfmcType]);
    const activeJfmc = jfmcTypes.find(
        (item) => item._id === selectedJfmcType
    );
    /* -------------------------------------- */
    const badge = (value, type) => {
        const styles = {
            total: {
                bg: "#EEF2FF",
                color: "#3730A3",
            },
            held: {
                bg: "#DCFCE7",
                color: "#166534",
            },
            pending: {
                bg: "#FEE2E2",
                color: "#991B1B",
            },
        };
        const style = styles[type];
        return (
            <span
                style={{ display: "inline-block", minWidth: 40, textAlign: "center", padding: "4px 10px", borderRadius: 20, background: style.bg, color: style.color, fontWeight: 600, fontSize: 12, }}>
                {value}
            </span>
        );
    };
    /* ---------------- Summary ---------------- */

    const totals = meetingData.reduce(
        (acc, item) => {
            acc.totalVfcs += item.totalVfcs || 0;
            acc.added += item.added || 0;
            acc.notAdded += item.notAdded || 0;
            acc.totalBankBalance += Number(item.totalBankBalance || 0);
            return acc;
        },
        {
            totalVfcs: 0,
            added: 0,
            notAdded: 0,
            totalBankBalance: 0,
        }
    );
    const toggleDivision = (id) => {
        setExpandedDivisions(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };
    const toggleRange = (id) => {
        setExpandedRanges(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };
    const formatLastUpdated = (date) => {
        if (!date) return "-";

        return new Date(date).toLocaleString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };
    /* -------------------------------------- */
    const exportBankBalanceReport = () => {
        if (!meetingData || meetingData.length === 0) {
            alert("No data available to export");
            return;
        }
        const selectedJFMCType = jfmcTypes.find(
            (type) => String(type._id) === String(selectedJfmcType)
        );
        const jfmcTypeName =
            selectedJFMCType?.jfmcTypeName || "JFMC";
        const totalJfmcHeading =
            `Total JFMC (${jfmcTypeName})`;
        const completedJfmcHeading =
            `Balance Added JFMC (${jfmcTypeName})`;
        const notCompletedJfmcHeading =
            `Balance Not Added JFMC (${jfmcTypeName})`;
        let reportYear;
        let reportMonth;
        if (
            typeof month === "string" &&
            /^\d{4}-\d{2}$/.test(month)
        ) {
            const [y, m] = month.split("-");
            reportYear = Number(y);
            reportMonth = Number(m);
        } else {
            reportYear = Number(year);
            reportMonth = Number(month);
        }
        const monthDate = new Date(
            reportYear,
            reportMonth - 1,
            1
        );
        const monthName = monthDate.toLocaleString("en-US", {
            month: "long",
        });
        const reportTitle =
            `Bank Balance Summary for the Month of ${monthName} ${reportYear}`;
        const excelData = [];
        const isCircleLevel =
            !selectedCircle &&
            !selectedDivision &&
            !selectedRange;
        const isDivisionLevel =
            !!selectedCircle &&
            !selectedDivision &&
            !selectedRange;
        const isRangeLevel =
            !!selectedDivision &&
            !selectedRange;
        const isVfcLevel =
            !!selectedRange;
        meetingData.forEach((item) => {
            if (isCircleLevel) {
                (item.completedDivisions || []).forEach((division) => {
                    excelData.push({
                        Circle: item.circleName,
                        Division: division.divisionName,
                        [totalJfmcHeading]: Number(division.totalVfcs || 0),
                        [completedJfmcHeading]: Number(division.added || 0),
                        [notCompletedJfmcHeading]: Number(division.notAdded || 0),
                        "Total Bank Balance": Number(division.totalBankBalance || 0),
                    });
                });
                // divisions having only pending balances
                (item.pendingDivisions || []).forEach((division) => {
                    const exists = (item.completedDivisions || []).some(
                        d => String(d._id) === String(division._id)
                    );
                    if (!exists) {
                        excelData.push({
                            Circle: item.circleName,
                            Division: division.divisionName,
                            [totalJfmcHeading]: Number(division.totalVfcs || 0),
                            [completedJfmcHeading]: Number(division.added || 0),
                            [notCompletedJfmcHeading]: Number(division.notAdded || 0),
                            "Total Bank Balance": Number(division.totalBankBalance || 0),
                        });
                    }
                });
                return;
            }
            if (isDivisionLevel) {
                excelData.push({
                    Division: item.divisionName,
                    [totalJfmcHeading]: Number(item.totalVfcs || 0),
                    [completedJfmcHeading]: Number(item.added || 0),
                    [notCompletedJfmcHeading]: Number(item.notAdded || 0),
                    "Total Bank Balance": Number(item.totalBankBalance || 0),
                });
                return;
            }
            if (isRangeLevel) {
                (item.completedVfcs || []).forEach((vfc) => {
                    excelData.push({
                        VFC: vfc.vfcName || vfc.name,
                        Status: "Added",
                        "Bank Balance": Number(vfc.bankBalance || 0),
                        "Date Of Execution": vfc.dateOfExecution
                            ? new Date(vfc.dateOfExecution).toLocaleDateString("en-GB")
                            : "-",
                    });
                });
                (item.pendingVfcs || []).forEach((vfc) => {
                    excelData.push({
                        VFC: vfc.vfcName || vfc.name,
                        Status: "Not Added",
                        "Bank Balance": 0,
                        "Date Of Execution": "-",
                    });
                });
                return;
            }
            if (isVfcLevel) {
                (item.completedVfcs || []).forEach((vfc) => {
                    excelData.push({
                        VFC: vfc.vfcName || vfc.name,
                        Status: "Added",
                        "Bank Balance": Number(vfc.bankBalance || 0),
                        "Date Of Execution": vfc.dateOfExecution
                            ? new Date(vfc.dateOfExecution).toLocaleDateString("en-GB")
                            : "-",
                    });
                });
                (item.pendingVfcs || []).forEach((vfc) => {
                    excelData.push({
                        VFC: vfc.vfcName || vfc.name,
                        Status: "Not Added",
                        "Bank Balance": 0,
                        "Date Of Execution": "-",
                    });
                });
                return;
            }
        });
        if (excelData.length === 0) {
            alert("No data available to export");
            return;
        }

        const headers = Object.keys(excelData[0]);

        const rows = excelData.map((row) =>
            headers.map((header) => row[header])
        );

        const worksheet = XLSX.utils.aoa_to_sheet([
            [reportTitle],
            [],
            headers,
            ...rows,
        ]);

        worksheet["!merges"] = [
            {
                s: { r: 0, c: 0 },
                e: { r: 0, c: headers.length - 1 },
            },
        ];

        worksheet["!cols"] = headers.map((header) => ({
            wch: Math.max(header.length + 3, 18),
        }));

        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(
            workbook,
            worksheet,
            "Bank Balance Review"
        );

        let level = "Circle";

        if (isDivisionLevel) level = "Division";
        if (isRangeLevel) level = "Range";
        if (isVfcLevel) level = "VFC";

        XLSX.writeFile(
            workbook,
            `Bank_Balance_${level}_${monthName}_${reportYear}.xlsx`
        );
    };
    if (loading) return <Preloader loading />;
    if (error) return <p className="text-danger">{error}</p>;
    return <div>
        <div className="rd-page-header">

            <div className="rd-header-left">
                <span className="page-title-icon">
                    <i className="mdi mdi-clipboard-check-multiple-outline" />
                </span>

                <div>
                    <h3 className="rd-page-title">
                        Bank Balance Review Dashboard
                    </h3>

                    <p className="rd-page-sub">
                        Month-wise Bank Balance Review
                    </p>
                </div>
            </div>

        </div>

        {/* ---------------- Filters ---------------- */}

        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", padding: "20px 24px", marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                <span style={{ width: 30, height: 30, background: "linear-gradient(135deg,#1B5E20,#43A047)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 14 }}>
                    <i className="mdi mdi-filter-variant" />
                </span>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#1A202C" }}>Filter Report</span>
                <span style={{ fontSize: 11, color: "#A0AEC0", marginLeft: 4 }}>Toggle filters and select a date to view records</span>
            </div>

            {/* Toggle pills */}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
                <TogglePill label="Circle" icon="mdi-circle-outline" checked={showCircle} disabled={showDivision} onChange={(e) => { setShowCircle(e.target.checked); if (!e.target.checked) setSelectedCircle(""); }} />
                <TogglePill label="Division" icon="mdi-domain" checked={showDivision} disabled={showCircle} onChange={(e) => { setShowDivision(e.target.checked); if (!e.target.checked) setSelectedDivision(""); }} />
            </div>
            {/* Conditional selects */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 12, marginBottom: 16 }}>
                <div >
                    <label style={labelStyle}>
                        <i
                            className="mdi mdi-calendar-month"
                            style={{ color: "#43A047" }}
                        />
                        Month
                    </label>

                    <input
                        type="month"
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                        style={inputStyle}
                    />
                </div>
                <div>
                    <label style={labelStyle}>
                        <i
                            className="mdi mdi-shape-outline"
                            style={{ color: "#43A047" }}
                        />
                        JFMC Type
                    </label>

                    <select
                        value={selectedJfmcType}
                        onChange={(e) =>
                            setSelectedJfmcType(e.target.value)
                        }
                        style={inputStyle}
                        onFocus={(e) =>
                            (e.target.style.borderColor = "#4CAF50")
                        }
                        onBlur={(e) =>
                            (e.target.style.borderColor = "#E2E8F0")
                        }
                    >
                        <option value="">— Select —</option>

                        {jfmcTypes.sort((a, b) => a.jfmcTypeName.localeCompare(b.jfmcTypeName)).map((j) => (
                            <option key={j._id} value={j._id}>
                                {j.jfmcTypeName}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 12, marginBottom: 16 }}>{showCircle && (
                <div>
                    <label style={labelStyle}><i className="mdi mdi-circle-outline" style={{ color: "#43A047" }} /> Circle</label>
                    <select value={selectedCircle} onChange={(e) => setSelectedCircle(e.target.value)} style={inputStyle} onFocus={(e) => e.target.style.borderColor = "#4CAF50"} onBlur={(e) => e.target.style.borderColor = "#E2E8F0"}>
                        <option value="">— Select —</option>
                        {circles.sort((a, b) => a.circleName.localeCompare(b.circleName)).map((c) => <option key={c._id} value={c._id}>{c.circleName}</option>)}
                    </select>
                </div>
            )}
                {showDivision && (
                    <div>
                        <label style={labelStyle}><i className="mdi mdi-domain" style={{ color: "#43A047" }} /> Division</label>
                        <select value={selectedDivision} onChange={(e) => setSelectedDivision(e.target.value)} style={inputStyle} onFocus={(e) => e.target.style.borderColor = "#4CAF50"} onBlur={(e) => e.target.style.borderColor = "#E2E8F0"}>
                            <option value="">— Select —</option>
                            {divisions.sort((a, b) => a.divisionName.localeCompare(b.divisionName)).map((d) => <option key={d._id} value={d._id}>{d.divisionName}</option>)}
                        </select>
                    </div>
                )}
            </div>
        </div>
        <div className="dashboard-sections">
            <div className="jfmc-section">
                <h3 className="jfmc-title">
                    {activeJfmc.jfmcTypeName} — Compliance Overview
                </h3>
                <div
                    key={activeJfmc.jfmcTypeId}
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))",
                        gap: 16,
                    }}
                >
                    {/* Total VFC */}
                    <div className="stat-card card-purple stat-centered">
                        <div className="stat-icon-big icon-purple">
                            <i className="fas fa-users-cog" />
                        </div>
                        <div className="stat-title">
                            Total JFMC
                        </div>
                        <div
                            style={{
                                fontSize: 34,
                                fontWeight: 700,
                                marginTop: 12,
                            }}
                        >
                            {totals.totalVfcs}
                        </div>
                    </div>
                    {/* Meetings Held */}
                    <div className="stat-card card-green stat-centered">
                        <div className="stat-icon-big icon-green">
                            <i className="fas fa-check-circle" />
                        </div>
                        <div className="stat-title">
                            Balance Added
                        </div>
                        <div
                            style={{
                                fontSize: 34,
                                fontWeight: 700,
                                marginTop: 12,
                            }}
                        >
                            {totals.added}
                        </div>
                    </div>
                    {/* Meetings Not Held */}
                    <div className="stat-card card-orange stat-centered">
                        <div className="stat-icon-big icon-orange">
                            <i className="fas fa-times-circle" />
                        </div>
                        <div className="stat-title">
                            Balance Not Added
                        </div>
                        <div
                            style={{
                                fontSize: 34,
                                fontWeight: 700,
                                marginTop: 12,
                            }}
                        >
                            {totals.notAdded}
                        </div>
                    </div>
                    <div className="stat-card card-teal stat-centered">
                        <div className="stat-icon-big icon-teal">
                            <i className="fas fa-university" />
                        </div>
                        <div className="stat-title">
                            Total Bank Balance
                        </div>
                        <div
                            style={{
                                fontSize: 28,
                                fontWeight: 700,
                                marginTop: 12,
                            }}
                        >
                            ₹ {totals.totalBankBalance.toLocaleString("en-IN", {
                                minimumFractionDigits: 2,
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
        {
            selectedLevel !== "circle" && (
                <button
                    className="btn btn-outline-secondary mb-3"
                    onClick={() => {
                        if (selectedLevel === "vfc") {
                            setSelectedRange("");
                            setSelectedLevel("range");
                            return;
                        }
                        if (selectedLevel === "range") {
                            setSelectedDivision("");
                            setSelectedLevel("division");
                            return;
                        }
                        if (selectedLevel === "division") {
                            setSelectedCircle("");
                            setSelectedLevel("circle");
                            return;
                        }
                    }}
                >
                    <i className="mdi mdi-arrow-left" />
                    Back
                </button>
            )
        }
        {/* ---------------- Summary Table ---------------- */}
        <div
            style={{
                marginTop: 24,
                background: "#fff",
                borderRadius: 12,
                border: "1px solid #E2E8F0",
                boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
                overflow: "hidden",
            }}
        >
            {/* Card Header */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: "14px 20px",
                    borderBottom: "1px solid #EDF2F7",
                }}
            >
                <span
                    style={{
                        width: 36,
                        height: 36,
                        background: "linear-gradient(135deg,#1B5E20,#43A047)",
                        borderRadius: 9,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                        fontSize: 16,
                    }}
                >
                    <i className="mdi mdi-table-large" />
                </span>
                <div>
                    <div
                        style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: "#1A202C",
                        }}
                    >
                        Bank Balance Summary
                    </div>
                    <div
                        style={{
                            fontSize: 11,
                            color: "#A0AEC0",
                        }}
                    >
                        {selectedRange
                            ? "VFC Wise"
                            : selectedDivision
                                ? "Range Wise"
                                : selectedCircle
                                    ? "Division Wise"
                                    : "Circle Wise"}
                    </div>
                </div>
                {/* Button at the end/right */}
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        marginLeft: "auto",
                    }}
                    className="jfmc-switch"
                >
                    <button
                        onClick={exportBankBalanceReport}
                        className="btn btn-success"
                    >
                        Export Excel
                    </button>
                </div>
            </div>
            {/* Table */}
            <div style={{ overflowX: "auto" }}>
                <table
                    style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        minWidth: "1200px",
                    }}
                >
                    <thead>
                        <tr
                            style={{
                                background: "#2E7D32",
                                color: "#fff",
                            }}
                        >
                            <th style={{ padding: 12, textAlign: "left" }}>
                                {selectedRange
                                    ? "VFC"
                                    : selectedDivision
                                        ? "Range"
                                        : selectedCircle
                                            ? "Division"
                                            : "Circle"}
                            </th>
                            {selectedRange ? (
                                <>
                                    <th style={{ padding: 12, textAlign: "center" }}>
                                        Status
                                    </th>
                                    <th style={{ padding: 12, textAlign: "right" }}>
                                        Bank Balance
                                    </th>
                                    <th style={{ padding: 12, textAlign: "center" }}>
                                        Date Of Execution
                                    </th>
                                </>
                            ) : (
                                <>
                                    <th style={{ padding: 12, textAlign: "center" }}>
                                        Total JFMC
                                    </th>
                                    <th style={{ padding: 12, textAlign: "center" }}>
                                        Added
                                    </th>
                                    <th style={{ padding: 12, textAlign: "center" }}>
                                        Pending
                                    </th>
                                    <th style={{ padding: 12, textAlign: "right" }}>
                                        Total Bank Balance
                                    </th>
                                    <th style={{ padding: 12 }}>
                                        {selectedCircle
                                            ? "Range Progress"
                                            : selectedDivision
                                                ? "VFC Progress"
                                                : "Division Progress"}
                                    </th>
                                </>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {meetingData.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={selectedRange ? 4 : 7}
                                    style={{
                                        textAlign: "center",
                                        padding: 30,
                                        color: "#999",
                                    }}
                                >
                                    No Records Found
                                </td>
                            </tr>
                        ) : (
                            meetingData.map((item, index) => (
                                <tr
                                    key={item._id}
                                    style={{
                                        background: index % 2 === 0 ? "#fff" : "#F8FAFD",
                                    }}
                                >
                                    <td
                                        style={{
                                            padding: "12px 15px",
                                            fontWeight: 600,
                                            color: "#1565C0",
                                        }}
                                    >
                                        <i
                                            className="mdi mdi-map-marker-outline"
                                            style={{ marginRight: 5 }}
                                        />
                                        {item.circleName}
                                        {item.lastUpdated && (
                                            <div
                                                style={{
                                                    fontSize: 11,
                                                    color: "#6B7280",
                                                    marginTop: 4,
                                                }}
                                            >
                                                Last Updated :{" "}
                                                {new Date(item.lastUpdated).toLocaleDateString("en-GB")}
                                            </div>
                                        )}
                                    </td>
                                    {selectedRange ? (
                                        <>
                                            <td style={{ textAlign: "center" }}>
                                                <span
                                                    style={{
                                                        display: "inline-block",
                                                        padding: "5px 12px",
                                                        borderRadius: 20,
                                                        fontSize: 12,
                                                        fontWeight: 600,
                                                        background: item.added
                                                            ? "#DCFCE7"
                                                            : "#FEE2E2",
                                                        color: item.added
                                                            ? "#166534"
                                                            : "#991B1B",
                                                    }}
                                                >
                                                    {item.added ? "Added" : "Not Added"}
                                                </span>
                                            </td>
                                            <td
                                                style={{
                                                    textAlign: "right",
                                                    paddingRight: 15,
                                                    fontWeight: 600,
                                                }}
                                            >
                                                ₹{" "}
                                                {Number(item.bankBalance || 0).toLocaleString(
                                                    "en-IN",
                                                    {
                                                        minimumFractionDigits: 2,
                                                    }
                                                )}
                                            </td>
                                            <td style={{ textAlign: "center" }}>
                                                {item.dateOfExecution
                                                    ? new Date(
                                                        item.dateOfExecution
                                                    ).toLocaleDateString("en-GB")
                                                    : "-"}
                                            </td>
                                        </>
                                    ) : (
                                        <>
                                            <td style={{ textAlign: "center" }}>
                                                {badge(item.totalVfcs, "total")}
                                            </td>
                                            <td style={{ textAlign: "center" }}>
                                                {badge(item.added, "held")}
                                            </td>
                                            <td style={{ textAlign: "center" }}>
                                                {badge(item.notAdded, "pending")}
                                            </td>
                                            <td
                                                style={{
                                                    textAlign: "right",
                                                    paddingRight: 15,
                                                    fontWeight: 600,
                                                }}
                                            >
                                                ₹{" "}
                                                {Number(
                                                    item.totalBankBalance || 0
                                                ).toLocaleString("en-IN", {
                                                    minimumFractionDigits: 2,
                                                })}
                                            </td>
                                            <td style={{ padding: 12, minWidth: 520 }}>
                                                {!selectedCircle ? (
                                                    (() => {
                                                        const completedDivisionIds = new Set(
                                                            (item.completedDivisions || []).map(d => d._id)
                                                        );
                                                        const divisions = [
                                                            ...(item.completedDivisions || []).map(d => ({
                                                                ...d,
                                                                status: "completed"
                                                            })),
                                                            ...(item.pendingDivisions || [])
                                                                .filter(d => !completedDivisionIds.has(d._id))
                                                                .map(d => ({
                                                                    ...d,
                                                                    status: "pending"
                                                                }))
                                                        ];
                                                        return divisions.map(division => (
                                                            <div
                                                                key={division._id}
                                                                style={{
                                                                    marginBottom: 15,
                                                                    border: "1px solid #E5E7EB",
                                                                    borderRadius: 10,
                                                                    overflow: "hidden"
                                                                }}
                                                            >
                                                                {/* Division Header */}
                                                                <div
                                                                    onClick={() => toggleDivision(division._id)}
                                                                    style={{
                                                                        display: "flex",
                                                                        justifyContent: "space-between",
                                                                        alignItems: "center",
                                                                        padding: "12px 15px",
                                                                        cursor: "pointer",
                                                                        background:
                                                                            division.status === "completed"
                                                                                ? "#ECFDF5"
                                                                                : "#FEF2F2"
                                                                    }}
                                                                >
                                                                    <div>
                                                                        <div
                                                                            style={{
                                                                                fontWeight: 700,
                                                                                color:
                                                                                    division.status === "completed"
                                                                                        ? "#166534"
                                                                                        : "#991B1B"
                                                                            }}
                                                                        >
                                                                            Division: {division.divisionName}
                                                                        </div>
                                                                        <div
                                                                            style={{
                                                                                fontSize: 12,
                                                                                color: "#666"
                                                                            }}
                                                                        >
                                                                            Added {division.added}/{division.totalVfcs}
                                                                        </div>
                                                                        {division.lastUpdated && (
                                                                            <div
                                                                                style={{
                                                                                    fontSize: 11,
                                                                                    color: "#6B7280",
                                                                                    marginTop: 3,
                                                                                }}
                                                                            >
                                                                                Last Updated :{" "}
                                                                                {new Date(division.lastUpdated).toLocaleDateString("en-GB")}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    <div
                                                                        style={{
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            gap: 15
                                                                        }}
                                                                    >
                                                                        <strong>
                                                                            ₹ {Number(
                                                                                division.totalBankBalance || 0
                                                                            ).toLocaleString("en-IN")}
                                                                        </strong>
                                                                        <i
                                                                            className={`mdi ${expandedDivisions[division._id]
                                                                                ? "mdi-chevron-up"
                                                                                : "mdi-chevron-down"
                                                                                }`}
                                                                            style={{ fontSize: 22 }}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                {/* Division Details */}
                                                                {expandedDivisions[division._id] && (
                                                                    <>
                                                                        {(division.completedRanges || []).map(range => (
                                                                            <div
                                                                                key={range._id}
                                                                                style={{
                                                                                    borderTop: "1px solid #E5E7EB"
                                                                                }}
                                                                            >
                                                                                {/* Range Header */}
                                                                                <div
                                                                                    onClick={() => toggleRange(range._id)}
                                                                                    style={{
                                                                                        display: "flex",
                                                                                        justifyContent: "space-between",
                                                                                        padding: "10px 18px",
                                                                                        cursor: "pointer",
                                                                                        background: "#FAFAFA"
                                                                                    }}
                                                                                >
                                                                                    <div>
                                                                                        <strong>
                                                                                            Range : {range.rangeName}
                                                                                        </strong>
                                                                                        <div
                                                                                            style={{
                                                                                                fontSize: 12,
                                                                                                color: "#666"
                                                                                            }}
                                                                                        >
                                                                                            Added {range.added}/{range.totalVfcs}
                                                                                        </div>
                                                                                        {range.lastUpdated && (
                                                                                            <div
                                                                                                style={{
                                                                                                    fontSize: 11,
                                                                                                    color: "#6B7280",
                                                                                                    marginTop: 3,
                                                                                                }}
                                                                                            >
                                                                                                Last Updated :{" "}
                                                                                                {new Date(range.lastUpdated).toLocaleDateString("en-GB")}
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                    <div
                                                                                        style={{
                                                                                            display: "flex",
                                                                                            gap: 15,
                                                                                            alignItems: "center"
                                                                                        }}
                                                                                    >
                                                                                        <strong>
                                                                                            ₹ {Number(
                                                                                                range.totalBankBalance || 0
                                                                                            ).toLocaleString("en-IN")}
                                                                                        </strong>
                                                                                        <i
                                                                                            className={`mdi ${expandedRanges[range._id]
                                                                                                ? "mdi-chevron-up"
                                                                                                : "mdi-chevron-down"
                                                                                                }`}
                                                                                        />
                                                                                    </div>
                                                                                </div>
                                                                                {/* VFC Details */}
                                                                                {expandedRanges[range._id] && (
                                                                                    <div
                                                                                        style={{
                                                                                            padding: "8px 25px"
                                                                                        }}
                                                                                    >
                                                                                        {(range.completedVfcs || []).map(vfc => (
                                                                                            <div
                                                                                                style={{
                                                                                                    display: "flex",
                                                                                                    justifyContent: "space-between",
                                                                                                    alignItems: "center",
                                                                                                    padding: "4px 0",
                                                                                                    fontSize: 12,
                                                                                                }}
                                                                                            >
                                                                                                <span>
                                                                                                    ✓ {vfc.vfcName}
                                                                                                </span>
                                                                                                <div style={{ textAlign: "right" }}>
                                                                                                    <div>
                                                                                                        ₹ {Number(vfc.bankBalance || 0).toLocaleString("en-IN")}
                                                                                                    </div>
                                                                                                    {vfc.dateOfExecution && (
                                                                                                        <div
                                                                                                            style={{
                                                                                                                fontSize: 11,
                                                                                                                color: "#6B7280",
                                                                                                            }}
                                                                                                        >
                                                                                                            {new Date(vfc.dateOfExecution).toLocaleDateString("en-GB")}
                                                                                                        </div>
                                                                                                    )}
                                                                                                </div>
                                                                                            </div>
                                                                                        ))}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        ))}
                                                                        {(division.pendingRanges || []).map(range => (
                                                                            <div
                                                                                key={range._id}
                                                                                style={{
                                                                                    padding: "10px 18px",
                                                                                    borderTop: "1px solid #F3F4F6",
                                                                                    background: "#FFF7F7",
                                                                                    display: "flex",
                                                                                    justifyContent: "space-between",
                                                                                    color: "#B91C1C",
                                                                                    fontWeight: 600
                                                                                }}
                                                                            >
                                                                                <span>Range : {range.rangeName}</span>
                                                                                <span>
                                                                                    0/{range.totalVfcs}
                                                                                </span>
                                                                            </div>
                                                                        ))}
                                                                    </>
                                                                )}
                                                            </div>
                                                        ));
                                                    })()
                                                ) : (
                                                    (() => {
                                                        const completedRangeIds = new Set(
                                                            (item.completedRanges || []).map(r => r._id)
                                                        );
                                                        const ranges = [
                                                            ...(item.completedRanges || []).map(r => ({
                                                                ...r,
                                                                status: "completed"
                                                            })),
                                                            ...(item.pendingRanges || [])
                                                                .filter(r => !completedRangeIds.has(r._id))
                                                                .map(r => ({
                                                                    ...r,
                                                                    status: "pending"
                                                                }))
                                                        ];
                                                        return ranges.map(range => (
                                                            <div
                                                                key={range._id}
                                                                style={{
                                                                    marginBottom: 15,
                                                                    border: "1px solid #E5E7EB",
                                                                    borderRadius: 10,
                                                                    overflow: "hidden"
                                                                }}
                                                            >
                                                                <div
                                                                    onClick={() => toggleRange(range._id)}
                                                                    style={{
                                                                        display: "flex",
                                                                        justifyContent: "space-between",
                                                                        padding: "12px 15px",
                                                                        cursor: "pointer",
                                                                        background:
                                                                            range.status === "completed"
                                                                                ? "#ECFDF5"
                                                                                : "#FEF2F2"
                                                                    }}
                                                                >
                                                                    <div>
                                                                        <strong>Range : {range.rangeName}</strong>
                                                                        <div
                                                                            style={{
                                                                                fontSize: 12,
                                                                                color: "#666"
                                                                            }}
                                                                        >
                                                                            Added {range.added}/{range.totalVfcs}
                                                                        </div>
                                                                    </div>
                                                                    <div
                                                                        style={{
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            gap: 15
                                                                        }}
                                                                    >
                                                                        <strong>
                                                                            ₹ {Number(
                                                                                range.totalBankBalance || 0
                                                                            ).toLocaleString("en-IN")}
                                                                        </strong>
                                                                        <i
                                                                            className={`mdi ${expandedRanges[range._id]
                                                                                ? "mdi-chevron-up"
                                                                                : "mdi-chevron-down"
                                                                                }`}
                                                                        />
                                                                    </div>
                                                                </div>
                                                                {expandedRanges[range._id] && (
                                                                    <div
                                                                        style={{
                                                                            padding: "8px 25px"
                                                                        }}
                                                                    >
                                                                        {(range.completedVfcs || []).map(vfc => (
                                                                            <div
                                                                                style={{
                                                                                    display: "flex",
                                                                                    justifyContent: "space-between",
                                                                                    alignItems: "center",
                                                                                    padding: "4px 0",
                                                                                    fontSize: 12,
                                                                                }}
                                                                            >
                                                                                <span>
                                                                                    ✓ {vfc.vfcName}
                                                                                </span>
                                                                                <div style={{ textAlign: "right" }}>
                                                                                    <div>
                                                                                        ₹ {Number(vfc.bankBalance || 0).toLocaleString("en-IN")}
                                                                                    </div>
                                                                                    {vfc.dateOfExecution && (
                                                                                        <div
                                                                                            style={{
                                                                                                fontSize: 11,
                                                                                                color: "#6B7280",
                                                                                            }}
                                                                                        >
                                                                                            {new Date(vfc.dateOfExecution).toLocaleDateString("en-GB")}
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ));
                                                    })
                                                )}
                                            </td>
                                        </>
                                    )}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    </div>
};

export default BankBalanceReview;
